'use client'

import { useEffect, useRef } from 'react'

/**
 * Streams the microphone's input level (0–1, smoothed) to `onLevel` once per animation frame
 * while `active`. For live "you're being heard" feedback during dictation: the ElevenLabs
 * Scribe client owns its own mic stream and exposes no level, so this opens a second, meter-only
 * capture of the default input (browsers share the device) and releases it when inactive.
 *
 * `onLevel` runs every frame — write straight to the DOM (a CSS variable), don't set state.
 */
export function useMicLevel(active: boolean, onLevel: (level: number) => void) {
  const onLevelRef = useRef(onLevel)
  onLevelRef.current = onLevel

  useEffect(() => {
    if (!active || typeof navigator === 'undefined' || !navigator.mediaDevices) return

    let cancelled = false
    let frame: number | null = null
    let stream: MediaStream | null = null
    let context: AudioContext | null = null

    void (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          // Same processing as the Scribe capture, so both see the same signal.
          audio: { echoCancellation: true, noiseSuppression: true },
        })
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop())
          return
        }
        context = new AudioContext()
        const analyser = context.createAnalyser()
        analyser.fftSize = 512
        context.createMediaStreamSource(stream).connect(analyser)
        const samples = new Float32Array(analyser.fftSize)

        let smoothed = 0
        const tick = () => {
          analyser.getFloatTimeDomainData(samples)
          let sum = 0
          for (const s of samples) sum += s * s
          const rms = Math.sqrt(sum / samples.length)
          // Speech RMS sits around 0.02–0.2; map that onto 0–1 on a log-ish curve.
          const level = Math.min(1, Math.max(0, (Math.log10(rms + 1e-4) + 2.5) / 1.8))
          // Fast attack, slow release: jumps with a syllable, eases off between words.
          smoothed =
            level > smoothed ? level * 0.6 + smoothed * 0.4 : level * 0.12 + smoothed * 0.88
          onLevelRef.current(smoothed)
          frame = requestAnimationFrame(tick)
        }
        tick()
      } catch {
        // No permission / no device: Scribe's own capture reports that; the meter just stays off.
      }
    })()

    return () => {
      cancelled = true
      if (frame !== null) cancelAnimationFrame(frame)
      stream?.getTracks().forEach((track) => track.stop())
      void context?.close()
      onLevelRef.current(0)
    }
  }, [active])
}
